import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { showBasicSnackBar } from '@aquariuxfintech/aqx-trader-react-ui/snack-bar';

import { useRequestOtpTimer } from '@aqx-core/common/hooks/sign-up/use-request-otp-timer';
import { useSignupCrm } from '@aqx-core/common/services/sign-up-crm';
import { signUpCrmViewController } from '@aqx-core/common/services/sign-up-crm/controller';
import { EUISnackBarMessageType } from '@aqx-core/common/services/snackbar';

const OTP_LENGTH = 6;

export const useSignUpVerification = () => {
  const { t } = useTranslation();
  const { loading: isLoading } = useSignupCrm();
  const { startTimer, remainingTimeText, isResendDisabled } = useRequestOtpTimer({ isSubmitting: isLoading });

  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');

  const onClickResend = useCallback(() => {
    if (!isResendDisabled) {
      signUpCrmViewController.requestOtp({
        onSuccess: () => {
          startTimer();
        },
        onError: (message: string) =>
          showBasicSnackBar({
            message,
            messageType: EUISnackBarMessageType.ERROR
          })
      });
    }
  }, [isResendDisabled, startTimer]);

  const onOtpChange = (pin: string) => {
    setOtp(pin);
    setError('');
  };

  const onSubmit = async () => {
    if (otp.length !== OTP_LENGTH) {
      setError(t('Please input a valid 6-digit code'));
      return;
    }
    const params = { otp };
    const onError = (message: string) => {
      setError(message);
    };

    signUpCrmViewController.completeSignUp({ params, onError });
  };

  const isButtonDisabled = useMemo(() => {
    return otp.length !== OTP_LENGTH;
  }, [otp]);

  useEffect(() => {
    startTimer();
  }, [startTimer]);

  return {
    isLoading,
    onOtpChange,
    remainingTimeText,
    isResendDisabled,
    onSubmit,
    onClickResend,
    error,
    isButtonDisabled
  };
};
